import template from "./helpers/templates";
import ajax from "./helpers/ajax";
import user from "./User";
import { isAuthError } from "./helpers/errors";
import { addNotification } from "./helpers/notifications";
import { getErrorMessage } from "./helpers/errors";

const Course = {
  completeContainer: null,
  questionContainer: null,
  outcomeContainer: null,
  isLesson: false,
  hasQuestion: false,
  introData: false,
  lessonData: false,
  questionData: false,
  async init() {
    if (!window.digiteketData.course) {
      return;
    }

    const course = window.digiteketData.course,
      guest = course.guest;

    Course.isLesson = !!course.lesson;
    Course.isGuest = guest; // Might still come in handy.

    if (Course.isLesson) {
      Course.lessonData = course.lesson;
      Course.questionData = course.lesson.question;
      Course.hasQuestion = Course.questionData || false;
    } else {
      Course.introData = course.intro;
    }

    if (Course.hasQuestion) {
      document
        .querySelector("body")
        .addEventListener("change", (e) =>
          Course.onQuestionAnswerChange.call(this, e)
        );

      this.questionContainer = document.querySelector(
        ".single-course__question"
      );

      await template.preload("component-question").then(() => {
        if (Course.isGuest) {
          // This will remove stored "checked" attributes on the inputs from the browser.
          Course.renderQuestion();
        }
      });
    } else {
      document
        .querySelector("body")
        .addEventListener("submit", (e) => Course.onComplete.call(this, e));

      this.completeContainer = document.querySelector(
        ".single-course__complete"
      );

      await template.preload("component-complete");
    }

    return this;
  },
  async renderQuestion(data) {
    return template.renderTo(
      "component-question",
      { ...Course.questionData, ...data },
      this.questionContainer
    );
  },
  async renderIntroComplete(data) {
    return template.renderTo(
      "component-complete",
      { ...(Course.introData.complete || {}), ...data },
      this.completeContainer
    );
  },
  async renderLessonComplete(data) {
    return template.renderTo(
      "component-complete",
      { ...(Course.lessonData.complete || {}), ...data },
      this.completeContainer
    );
  },
  onComplete(e) {
    if (
      !(
        e.target.tagName.toLowerCase() === "form" &&
        e.target.getAttribute("name") == "component-complete"
      )
    ) {
      return;
    }

    e.preventDefault();

    const form = e.target;

    const data = Array.from(new FormData(form).entries()).reduce(
      (data, [key, value]) => {
        return {
          ...data,
          [key]: value,
        };
      },
      {}
    );

    if (Course.isLesson) {
      Course.markLessonCompleted(data.course_post_id, data.lesson_id);
    } else {
      Course.markIntroCompleted(data.course_post_id);
    }
  },
  onQuestionAnswerChange(e) {
    if (
      !(
        e.target.tagName.toLowerCase() === "input" &&
        e.target.classList.contains("component-question__answer-input")
      )
    ) {
      return;
    }

    const form = e.target.closest("form");

    const data = Array.from(new FormData(form).entries()).reduce(
      (data, [key, value]) => {
        return {
          ...data,
          [key]: value,
        };
      },
      {}
    );

    const args = [data.answer, data.course_post_id];

    if (data.lesson_id) {
      args.push(data.lesson_id);
    }

    Course.validateQuestionAnswer(...args);
  },
  validateQuestionAnswer(answerId, courseId, lessonId) {
    const request = {
      course_post_id: courseId,
      answer: answerId,
    };

    if (lessonId) {
      request.lesson_id = lessonId;
    }

    Course.questionContainer.classList.add("component-question--is-loading");

    const inputs = Course.questionContainer.querySelectorAll("input");

    inputs.forEach((input) => {
      input.setAttribute("disabled", "disabled");
    });

    return ajax
      .post("digiteket_validate_lesson_question_answer", request)
      .then((response) => {
        const answerClass = response.data?.correct
          ? "component-question__answer--is-correct"
          : "component-question__answer--is-incorrect";

        Course.renderQuestion({
          has_completed: response.data.correct,
          outcome: response.data.message,
          outcome_class: response.data.correct
            ? "component-question__outcome--is-success"
            : "component-question__outcome--is-error",
          answers: Course.questionData.answers.map((answer) => {
            return {
              ...answer,
              class: answer.id === answerId ? answerClass : "",
              checked: answer.id === answerId,
            };
          }),
          next_action_url: response.data.next_action_url,
          next_action_text: response.data.next_action_text,
          next_action_disabled: response.data.next_action_disabled,
        });
      })
      .catch((error) => {
        if (isAuthError(error)) {
          // Show login modal
          user.login((error) => {
            if (!error) {
              // If login was successful, try to answer the question again
              Course.validateQuestionAnswer(answerId, courseId, lessonId);
            } else {
              // Otherwise, show error message
              addNotification(getErrorMessage(error));
            }
          });
        } else {
          // Show error message
          addNotification(getErrorMessage(error));
        }
      })
      .finally(() => {
        // find outcome/error/success message container
        Course.outcomeContainer = document.querySelector(
          ".component-question__outcome"
        );

        // set focus to error/success message for a11y
        if (Course.outcomeContainer) Course.outcomeContainer.focus();

        Course.questionContainer.classList.remove(
          "component-question--is-loading"
        );
      });
  },
  markIntroCompleted(courseId) {
    const request = {
      course_post_id: courseId,
    };

    Course.completeContainer.classList.add("component-complete--is-loading");

    const buttons = Course.completeContainer.querySelectorAll("button");

    buttons.forEach((button) => {
      button.setAttribute("disabled", "disabled");
    });

    return ajax
      .post("digiteket_mark_intro_as_completed", request)
      .then((response) => {
        Course.renderIntroComplete(response.data ?? {});
      })
      .catch((error) => {
        if (isAuthError(error)) {
          // Show login modal
          user.login((error) => {
            if (!error) {
              // If login was successful, try to mark intro as completed again
              Course.markIntroCompleted(courseId);
            } else {
              // Otherwise, show error message
              addNotification(getErrorMessage(error));
            }
          });
        } else {
          // Show error message
          addNotification(getErrorMessage(error));
        }
      })
      .finally(() => {
        Course.completeContainer.classList.remove(
          "component-complete--is-loading"
        );

        const buttons = Course.completeContainer.querySelectorAll("button");

        buttons.forEach((button) => {
          button.removeAttribute("disabled");
        });
      });
  },
  markLessonCompleted(courseId, lessonId) {
    const request = {
      course_post_id: courseId,
      lesson_id: lessonId,
    };

    Course.completeContainer.classList.add("component-complete--is-loading");

    const buttons = Course.completeContainer.querySelectorAll("button");

    buttons.forEach((button) => {
      button.setAttribute("disabled", "disabled");
    });

    return ajax
      .post("digiteket_mark_lesson_as_completed", request)
      .then((response) => {
        Course.renderLessonComplete(response.data ?? {});
      })
      .catch((error) => {
        if (isAuthError(error)) {
          // Show login modal
          user.login((error) => {
            if (!error) {
              // If login was successful, try to mark lesson as completed again
              Course.markLessonCompleted(courseId, lessonId);
            } else {
              // Otherwise, show error message
              addNotification(getErrorMessage(error));
            }
          });
        } else {
          // Show error message
          addNotification(getErrorMessage(error));
        }
      })
      .finally(() => {
        Course.completeContainer.classList.remove(
          "component-complete--is-loading"
        );

        const buttons = Course.completeContainer.querySelectorAll("button");

        buttons.forEach((button) => {
          button.removeAttribute("disabled");
        });
      });
  },
};

export default Course;
